import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { renderToStaticMarkup } from 'react-dom/server';
import { Pallet2DVisualizer } from './Pallet2DVisualizer';
import { calculatePalletLoading, PALLET_HEIGHT, PALLET_WEIGHT, PALLET_LENGTH, PALLET_WIDTH } from '../../utils/palletCalculation';
import { usePIM } from '../../context/PIMContext';
import { GhostButton } from '../ui/GhostButton';
import { PrimaryButton } from '../ui/PrimaryButton';
import { Input } from '../ui/Input';
import { Tooltip } from '../ui/Tooltip';
import { Printer, Calculator, X, Box, Square } from 'lucide-react';

export const PalletControls = ({ onArrangementSelected, selectedArrangement }) => {
    const { toast } = useToast();
    const {
        productData,
        activeCharge,
        setPalletCalculationResults,
        resetLayout,
        packagingProperties,
        setMiddleWidget,
        setPalletMiddleWidget,
        packagingGroups
    } = usePIM();

    // Inputs
    const [boxDimensions, setBoxDimensions] = useState({ l: '', w: '', h: '' });
    const [boxWeight, setBoxWeight] = useState('');
    const [maxHeight, setMaxHeight] = useState(1500);
    const [palletLength, setPalletLength] = useState(PALLET_LENGTH);
    const [palletWidth, setPalletWidth] = useState(PALLET_WIDTH);
    const [mixedOrientation, setMixedOrientation] = useState(true);
    const [includePalletWeight, setIncludePalletWeight] = useState(true);
    const [allowTipping, setAllowTipping] = useState(false);

    // New parameters
    const [maxPalletWeight, setMaxPalletWeight] = useState(1000);
    const [toleranceMm, setToleranceMm] = useState(0);
    const [overhangWidth, setOverhangWidth] = useState(0);
    const [overhangLength, setOverhangLength] = useState(0);

    // Packaging Group Selection
    const [selectedGroup, setSelectedGroup] = useState(null);

    // Initialize from Packaging Properties
    useEffect(() => {
        if (packagingProperties && packagingGroups) {
            // Helper to normalize strings
            const normalize = (str) => str ? str.replace(/\s+/g, ' ').trim().toLowerCase() : '';

            // 1. Find Master Carton Group
            const masterCartonGroup = packagingGroups.find(g =>
                normalize(g.name).includes('master') && normalize(g.name).includes('carton')
            );

            // Set default selection
            if (masterCartonGroup) {
                setSelectedGroup(masterCartonGroup);
            }

            // 2. Find Properties for the selected group (or Master Carton by default)
            let targetGroupName = masterCartonGroup ? masterCartonGroup.name : '';

            // Filter properties for this group
            const groupProps = packagingProperties.filter(p =>
                normalize(p.group_name) === normalize(targetGroupName)
            );

            // Fallback: If no group props found, use all properties (legacy behavior)
            const searchScope = groupProps.length > 0 ? groupProps : packagingProperties;

            // Find dimensions
            const dimProp = searchScope.find(p => {
                const name = normalize(p.name);
                return name.includes('dimension') || name.includes('maße') || name.includes('size');
            });

            // Find weight
            const weightProp = searchScope.find(p => {
                const name = normalize(p.name);
                return name.includes('weight') || name.includes('gross') || name.includes('gewicht');
            });

            let foundL, foundW, foundH, foundWeight;

            if (dimProp && dimProp.value) {
                const parts = dimProp.value.toLowerCase().split('x');
                if (parts.length === 3) {
                    foundL = parts[0].trim();
                    foundW = parts[1].trim();
                    foundH = parts[2].trim();
                }
            }

            // Fallback: Individual Properties
            if (!foundL || !foundW || !foundH) {
                const lProp = searchScope.find(p => {
                    const n = normalize(p.name);
                    return n.includes('length') || n.includes('länge') || n.includes('tief');
                });
                const wProp = searchScope.find(p => {
                    const n = normalize(p.name);
                    return n.includes('width') || n.includes('breite');
                });
                const hProp = searchScope.find(p => {
                    const n = normalize(p.name);
                    return (n.includes('height') || n.includes('höhe')) && !n.includes('weight');
                });

                if (lProp) foundL = lProp.value;
                if (wProp) foundW = wProp.value;
                if (hProp) foundH = hProp.value;
            }

            // Apply found values
            if (foundL || foundW || foundH) {
                setBoxDimensions({
                    l: foundL || '',
                    w: foundW || '',
                    h: foundH || ''
                });
            }

            if (weightProp && weightProp.value) {
                foundWeight = weightProp.value.replace(',', '.').trim();
                setBoxWeight(foundWeight);
            }

            // Auto-Calculate if we found all necessary data
            if (foundL && foundW && foundH && foundWeight) {
                try {
                    const results = calculatePalletLoading(
                        parseFloat(foundW), parseFloat(foundL), parseFloat(foundH), parseFloat(foundWeight),
                        parseFloat(maxHeight), mixedOrientation, includePalletWeight,
                        parseFloat(palletWidth), parseFloat(palletLength), allowTipping,
                        parseFloat(maxPalletWeight) || 0, parseFloat(toleranceMm) || 0, parseFloat(overhangWidth) || 0, parseFloat(overhangLength) || 0
                    );

                    // Inject color if group selected
                    if (masterCartonGroup && masterCartonGroup.color) {
                        results.forEach(res => {
                            res.color = masterCartonGroup.color;
                        });
                    }

                    setPalletCalculationResults(results);
                    if (results.length > 0) {
                        onArrangementSelected(results[0]);
                    } else {
                        onArrangementSelected(null);
                    }
                } catch (e) {
                    console.warn("Auto-calculation failed:", e);
                    setPalletCalculationResults([]);
                    onArrangementSelected(null);
                }
            }
        }
    }, [packagingProperties, packagingGroups]);

    // Update dimensions when group changes
    const handleGroupChange = (e) => {
        const groupId = parseInt(e.target.value);
        const group = packagingGroups.find(g => g.id === groupId);
        setSelectedGroup(group);

        if (group && packagingProperties) {
            // Helper to normalize strings
            const normalize = (str) => str ? str.replace(/\s+/g, ' ').trim().toLowerCase() : '';

            // Filter properties for this group
            const groupProps = packagingProperties.filter(p =>
                normalize(p.group_name) === normalize(group.name)
            );

            // Find dimensions
            const dimProp = groupProps.find(p => {
                const name = normalize(p.name);
                return name.includes('dimension') || name.includes('maße') || name.includes('size');
            });

            // Find weight - Broadened search
            const weightProp = groupProps.find(p => {
                const name = normalize(p.name);
                return name.includes('weight') || name.includes('gross') || name.includes('gewicht');
            });

            let newL = '', newW = '', newH = '', newWeight = '';

            if (dimProp && dimProp.value) {
                const parts = dimProp.value.toLowerCase().split('x');
                if (parts.length === 3) {
                    newL = parts[0].trim();
                    newW = parts[1].trim();
                    newH = parts[2].trim();
                }
            }

            // Fallback: Individual Properties
            if (!newL || !newW || !newH) {
                const lProp = groupProps.find(p => {
                    const n = normalize(p.name);
                    return n.includes('length') || n.includes('länge') || n.includes('tief');
                });
                const wProp = groupProps.find(p => {
                    const n = normalize(p.name);
                    return n.includes('width') || n.includes('breite');
                });
                const hProp = groupProps.find(p => {
                    const n = normalize(p.name);
                    return (n.includes('height') || n.includes('höhe')) && !n.includes('weight');
                });

                if (lProp) newL = lProp.value;
                if (wProp) newW = wProp.value;
                if (hProp) newH = hProp.value;
            }

            setBoxDimensions({ l: newL || '', w: newW || '', h: newH || '' });

            if (weightProp && weightProp.value) {
                newWeight = weightProp.value.replace(',', '.').trim();
                setBoxWeight(newWeight);
            } else {
                setBoxWeight('');
            }

            // Auto-Calculate immediately with new values
            if (newL && newW && newH && newWeight) {
                try {
                    const results = calculatePalletLoading(
                        parseFloat(newW), parseFloat(newL), parseFloat(newH), parseFloat(newWeight),
                        parseFloat(maxHeight), mixedOrientation, includePalletWeight,
                        parseFloat(palletWidth), parseFloat(palletLength), allowTipping,
                        parseFloat(maxPalletWeight) || 0, parseFloat(toleranceMm) || 0, parseFloat(overhangWidth) || 0, parseFloat(overhangLength) || 0
                    );

                    // Inject color
                    if (group && group.color) {
                        results.forEach(res => {
                            res.color = group.color;
                        });
                    }

                    setPalletCalculationResults(results);
                    if (results.length > 0) {
                        onArrangementSelected(results[0]);
                    } else {
                        onArrangementSelected(null);
                    }
                } catch (err) {
                    console.warn("Auto-calc on group change failed:", err);
                    setPalletCalculationResults([]);
                    onArrangementSelected(null);
                }
            }
        }
    };

    const handleCalculate = () => {
        try {
            const l = parseFloat(boxDimensions.l);
            const w = parseFloat(boxDimensions.w);
            const h = parseFloat(boxDimensions.h);
            const weight = parseFloat(boxWeight);
            const pL = parseFloat(palletLength);
            const pW = parseFloat(palletWidth);

            if (!l || !w || !h || !pL || !pW) {
                setPalletCalculationResults([]);
                onArrangementSelected(null);
                return;
            }

            const safeWeight = weight || 0;

            const calculatedResults = calculatePalletLoading(
                w, l, h, safeWeight, parseFloat(maxHeight), mixedOrientation, includePalletWeight,
                pW, pL, allowTipping,
                parseFloat(maxPalletWeight) || 0, parseFloat(toleranceMm) || 0, parseFloat(overhangWidth) || 0, parseFloat(overhangLength) || 0
            );

            // Inject color if group selected
            if (selectedGroup && selectedGroup.color) {
                calculatedResults.forEach(res => {
                    res.color = selectedGroup.color;
                });
            }

            // Update Context State
            setPalletCalculationResults(calculatedResults);

            if (calculatedResults.length > 0) {
                onArrangementSelected(calculatedResults[0]);
            } else {
                onArrangementSelected(null);
            }
        } catch (err) {
            console.warn("Calculation failed:", err);
            setPalletCalculationResults([]);
            onArrangementSelected(null);
        }
    };

    // Validation styling helpers
    const getInputClass = (val, required = true, isZeroAllowed = false) => {
        const num = parseFloat(val);
        const isInvalid = required ? (isNaN(num) || (!isZeroAllowed && num <= 0) || (isZeroAllowed && num < 0)) : false;
        return `w-full text-sm ${isInvalid ? 'border-orange-500/70 focus:border-orange-500 text-orange-200' : ''}`;
    };

    const handlePrint = () => {
        if (!selectedArrangement) return;

        // Robust Print Logic (Ported from script.js)
        const dateStr = new Date().toLocaleDateString('de-DE');
        // Render 2D Visualizer to SVG string
        const visualizerHtml = renderToStaticMarkup(
            <div style={{ width: '100%', height: '500px', display: 'flex', justifyContent: 'center' }}>
                <Pallet2DVisualizer arrangement={selectedArrangement} transparentBackground={true} />
            </div>
        );

        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Paletten-Beladungsplan - ${productData?.model || ''}</title>
                <style>
                    @page { size: auto; margin: 10mm; }
                    body { font-family: sans-serif; padding: 0; margin: 0; }
                    .print-header { text-align: center; margin-bottom: 10px; border-bottom: 2px solid #333; padding-bottom: 5px; }
                    .print-header h1 { margin: 0 0 5px 0; font-size: 16pt; text-transform: uppercase; }
                    .header-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 20px; text-align: left; font-size: 9pt; max-width: 90%; margin: 0 auto; }
                    .print-section { margin-bottom: 10px; page-break-inside: avoid; }
                    .print-section h2 { border-bottom: 1px solid #ccc; font-size: 12pt; margin-bottom: 5px; }
                    .compact-table { width: 100%; border-collapse: collapse; font-size: 9pt; }
                    .compact-table td { border: 1px solid #ccc; padding: 4px 8px; }
                    .compact-table tr:nth-child(even) { background-color: #f9f9f9; }
                    .visualizer-container { margin-top: 10px; text-align: center; border: 1px solid #eee; padding: 10px; border-radius: 8px; page-break-inside: avoid; }
                    .visualizer-container svg { max-height: 400px; width: auto; }
                    .print-footer { margin-top: 15px; font-size: 8pt; text-align: center; color: #666; border-top: 1px solid #eee; padding-top: 5px; }
                </style>
            </head>
            <body>
                <div class="print-header">
                    <h1>Europaletten-Beladungsplan</h1>
                    <div class="header-info-grid">
                        <div><strong>Modell:</strong> ${productData?.model || '-'}</div>
                        <div><strong>SKU:</strong> ${productData?.sku || '-'}</div>
                        <div><strong>Charge:</strong> ${activeCharge?.charge_id || '-'}</div>
                        <div><strong>Datum:</strong> ${dateStr}</div>
                    </div>
                </div>

                <div class="print-section">
                    <h2>Paletten-Anordnung: ${selectedArrangement.name}</h2>
                    <table class="compact-table">
                        <tr><td><strong>Kartongröße:</strong></td><td>${selectedArrangement.boxWidth} x ${selectedArrangement.boxLength} x ${selectedArrangement.boxHeight} mm</td></tr>
                        <tr><td><strong>Gewicht pro Karton:</strong></td><td>${boxWeight} kg</td></tr>
                        <tr><td><strong>Kartons pro Lage:</strong></td><td>${selectedArrangement.boxesPerLayer}</td></tr>
                        <tr><td><strong>Anzahl Lagen:</strong></td><td>${selectedArrangement.layers}</td></tr>
                        <tr><td><strong>Gesamthöhe:</strong></td><td>${selectedArrangement.totalHeight} mm</td></tr>
                        <tr><td><strong>Gesamtanzahl:</strong></td><td>${selectedArrangement.totalBoxes} Stück</td></tr>
                        <tr><td><strong>Gesamtgewicht:</strong></td><td>${selectedArrangement.totalWeight?.toFixed(2) || 0} kg</td></tr>
                        <tr><td><strong>Theor. Ausnutzung:</strong></td><td>${selectedArrangement.theoreticalVolumeUtilization?.toFixed(2) || 0} %</td></tr>
                    </table>
                </div>

                <div class="print-section">
                    <h2>Visualisierung (2D)</h2>
                    <div class="visualizer-container">
                        ${visualizerHtml}
                    </div>
                </div>

                <div class="print-footer">
                    Erstellt mit dem Europaletten-Rechner
                </div>
                <script>
                    window.onload = function() { setTimeout(function() { window.print(); }, 500); };
                </script>
            </body>
            </html>
        `;

        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.open();
            printWindow.document.write(printContent);
            printWindow.document.close();
        }
    };

    return (
        <div className="h-full flex flex-col gap-4">
            <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-bold text-stone-300 uppercase">Input Parameters</h3>
                <button
                    onClick={resetLayout}
                    className="p-1.5 rounded transition-colors border text-stone-400 border-transparent hover:bg-red-900/20 hover:text-red-400 hover:border-red-900/50"
                    aria-label="Close"
                >
                    <X size={16} />
                </button>
            </div>

            {/* Packaging Group Selection */}
            <div className="mb-2">
                <label className="text-xs text-stone-500 uppercase font-bold block mb-1">Packaging Group</label>
                <select
                    className="w-full bg-stone-800 border border-stone-700 rounded p-2 text-sm text-white focus:outline-none focus:border-blue-500"
                    value={selectedGroup?.id || ''}
                    onChange={handleGroupChange}
                >
                    <option value="">Select Group...</option>
                    {packagingGroups && packagingGroups.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                </select>
            </div>

            {/* Inputs */}
            <div className="grid grid-cols-4 gap-x-3 gap-y-2">
                <div className="col-span-2">
                    <label className="text-[10px] text-stone-500 uppercase font-bold block mb-1">Box Dimensions (L x W x H)</label>
                    <div className="flex gap-2">
                        <Input placeholder="L" value={boxDimensions.l} onChange={e => setBoxDimensions({ ...boxDimensions, l: e.target.value })} className={getInputClass(boxDimensions.l)} />
                        <Input placeholder="W" value={boxDimensions.w} onChange={e => setBoxDimensions({ ...boxDimensions, w: e.target.value })} className={getInputClass(boxDimensions.w)} />
                        <Input placeholder="H" value={boxDimensions.h} onChange={e => setBoxDimensions({ ...boxDimensions, h: e.target.value })} className={getInputClass(boxDimensions.h)} />
                    </div>
                </div>
                <div className="col-span-1">
                    <label className="text-[10px] text-stone-500 uppercase font-bold block mb-1">Weight (kg)</label>
                    <Input placeholder="kg" value={boxWeight} onChange={e => setBoxWeight(e.target.value)} className={getInputClass(boxWeight, true, true)} />
                </div>
                <div className="col-span-1">
                    <label className="text-[10px] text-stone-500 uppercase font-bold block mb-1">Max H (mm)</label>
                    <Input value={maxHeight} onChange={e => setMaxHeight(e.target.value)} className={getInputClass(maxHeight, true, false)} />
                </div>

                <div className="col-span-1">
                    <label className="text-[10px] text-stone-500 uppercase font-bold block mb-1">Pallet L (mm)</label>
                    <Input value={palletLength} onChange={e => setPalletLength(e.target.value)} className={getInputClass(palletLength)} />
                </div>
                <div className="col-span-1">
                    <label className="text-[10px] text-stone-500 uppercase font-bold block mb-1">Pallet W (mm)</label>
                    <Input value={palletWidth} onChange={e => setPalletWidth(e.target.value)} className={getInputClass(palletWidth)} />
                </div>
                <div className="col-span-1">
                    <label className="text-[10px] text-stone-500 uppercase font-bold block mb-1">Max Wgt (kg)</label>
                    <Input value={maxPalletWeight} onChange={e => setMaxPalletWeight(e.target.value)} placeholder="0" className="w-full text-sm" />
                </div>
                <div className="col-span-1">
                    <label className="text-[10px] text-stone-500 uppercase font-bold block mb-1">Tolerance</label>
                    <Input value={toleranceMm} onChange={e => setToleranceMm(e.target.value)} placeholder="0" className="w-full text-sm" />
                </div>

                <div className="col-span-1">
                    <label className="text-[10px] text-stone-500 uppercase font-bold block mb-1">Overhang L</label>
                    <Input value={overhangLength} onChange={e => setOverhangLength(e.target.value)} placeholder="0" className="w-full text-sm" />
                </div>
                <div className="col-span-1">
                    <label className="text-[10px] text-stone-500 uppercase font-bold block mb-1">Overhang W</label>
                    <Input value={overhangWidth} onChange={e => setOverhangWidth(e.target.value)} placeholder="0" className="w-full text-sm" />
                </div>
                <div className="col-span-2 flex flex-col justify-end pb-1 border-l border-stone-800 pl-3 ml-1">
                    <div className="flex items-center gap-3">
                        <Tooltip content="Erlaubt Drehung um 90°">
                            <label className="flex items-center gap-1.5 text-[11px] text-stone-300 cursor-pointer">
                                <input type="checkbox" checked={mixedOrientation} onChange={e => setMixedOrientation(e.target.checked)} className="accent-blue-500" />
                                Mixed Orient
                            </label>
                        </Tooltip>
                        <Tooltip content="Erlaubt senkrechtes Aufstellen">
                            <label className="flex items-center gap-1.5 text-[11px] text-stone-300 cursor-pointer">
                                <input type="checkbox" checked={allowTipping} onChange={e => setAllowTipping(e.target.checked)} className="accent-blue-500" />
                                Tipping
                            </label>
                        </Tooltip>
                        <label className="flex items-center gap-1.5 text-[11px] text-stone-300 cursor-pointer">
                            <input type="checkbox" checked={includePalletWeight} onChange={e => setIncludePalletWeight(e.target.checked)} className="accent-blue-500" />
                            Pallet Wgt
                        </label>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-4 gap-2 mt-3">
                <GhostButton
                    onClick={handleCalculate}
                    className="justify-center bg-blue-900/20 text-blue-100 border-blue-700 hover:bg-blue-800/30 hover:border-blue-500"
                >
                    <Calculator size={16} /> Calc
                </GhostButton>
                <GhostButton
                    onClick={handlePrint}
                    disabled={!selectedArrangement}
                    className={`justify-center ${!selectedArrangement ? 'opacity-50 cursor-not-allowed bg-stone-900/20 text-stone-500 border-stone-800' : 'bg-yellow-900/20 text-yellow-100 border-yellow-700 hover:bg-yellow-800/30 hover:border-yellow-500'}`}
                >
                    <Printer size={16} /> Print
                </GhostButton>
                <GhostButton
                    onClick={() => setPalletMiddleWidget('Pallet3DVisualizer')}
                    disabled={!selectedArrangement}
                    className={`justify-center ${!selectedArrangement ? 'opacity-50 cursor-not-allowed bg-stone-900/20 text-stone-500 border-stone-800' : 'bg-purple-900/20 text-purple-100 border-purple-700 hover:bg-purple-800/30 hover:border-purple-500'}`}
                >
                    <Box size={16} /> 3D
                </GhostButton>
                <GhostButton
                    onClick={() => setPalletMiddleWidget('Pallet2DVisualizer')}
                    disabled={!selectedArrangement}
                    className={`justify-center ${!selectedArrangement ? 'opacity-50 cursor-not-allowed bg-stone-900/20 text-stone-500 border-stone-800' : 'bg-green-900/20 text-green-100 border-green-700 hover:bg-green-800/30 hover:border-green-500'}`}
                >
                    <Square size={16} /> 2D
                </GhostButton>
            </div>

            <div className="mt-auto text-xs text-stone-500 text-center p-4">
                Results will appear in the main table below.
            </div>
        </div >
    );
};
